import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import * as normalActions from '../store/actions'
import * as likeActions from '../data/like_data'
import CommentThread from './commentThread'
import CommentComposer from './commentComposer'
import { useAuthGate } from './authPrompt'
import { scrollToComment } from '../utils/commentFocus'

const WatchComments = ({ postId, onClose, highlightId }) => {
  const dispatch = useDispatch()
  const { userid, requireAuth, promptEl } = useAuthGate()
  const pid = useSelector(state => state.authReduse.pid)
  const userPhoto = useSelector(state => state.authReduse.photo)
  const userEmail = useSelector(state => state.authReduse.email)
  const comments = useSelector(state => state.CommentsReduse.Comments || [])
  const [replyTo, setReplyTo] = useState(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    document.body.classList.add('watchCommentsOpen')
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setOpen(true))
    })
    return () => {
      window.cancelAnimationFrame(frame)
      document.body.classList.remove('watchCommentsOpen')
    }
  }, [])

  useEffect(() => {
    if (!postId) return undefined
    dispatch(likeActions.get_post(postId)).catch(() => {})
    return undefined
  }, [dispatch, postId])

  const closeSheet = () => {
    setOpen(false)
    window.setTimeout(onClose, 320)
  }

  const postComments = useMemo(
    () => comments.filter(comm => String(comm.postid) === String(postId)),
    [comments, postId]
  )

  useEffect(() => {
    if (!highlightId || !postComments.length) return undefined
    let tries = 0
    const tick = () => {
      if (scrollToComment(highlightId) || tries > 24) return
      tries += 1
      window.setTimeout(tick, 150)
    }
    const wait = window.setTimeout(tick, 80)
    return () => window.clearTimeout(wait)
  }, [highlightId, postComments.length])

  const repliesByParent = useMemo(() => postComments.reduce((map, comment) => {
    const parentKey = comment.parentId ? String(comment.parentId) : ''
    if (!parentKey) return map
    map[parentKey] = map[parentKey] || []
    map[parentKey].push(comment)
    return map
  }, {}), [postComments])

  const rootComments = useMemo(
    () => postComments.filter(comment => !comment.parentId),
    [postComments]
  )

  const handleAddComment = async ({ text, media }) => {
    if (!requireAuth()) return
    if ((!text && !media) || !userid || !pid || !postId) return
    await dispatch(normalActions.add_to_comments(text, userid, postId, userPhoto, userEmail, pid, replyTo?.id || null, media))
    setReplyTo(null)
  }

  const handleDeleteComment = comment => {
    if (!requireAuth()) return
    if (String(comment.userid) !== String(userid)) return
    dispatch(normalActions.delete_comment(comment.id, userid, pid))
  }

  const blockFeedScroll = event => {
    event.stopPropagation()
  }

  return createPortal(
    <div
      className={`watchCommentsOverlay${open ? ' isOpen' : ''}`}
      onClick={closeSheet}
      onWheel={blockFeedScroll}
      onTouchStart={blockFeedScroll}
      onTouchMove={event => {
        blockFeedScroll(event)
        if (!event.target.closest('.watchCommentsList')) event.preventDefault()
      }}
    >
      {promptEl}
      <div className="watchCommentsSheet" onClick={event => event.stopPropagation()}>
        <div className="watchCommentsHandle" aria-hidden="true" />
        <div className="watchCommentsHead">
          <strong>Comments</strong>
          <button type="button" onClick={closeSheet}>Close</button>
        </div>
        <div className="watchCommentsList">
          {rootComments.length === 0 && <p className="commentsEmpty">Be the first to comment</p>}
          {rootComments.map((comment, index) => (
            <CommentThread
              key={comment.id}
              comment={comment}
              repliesByParent={repliesByParent}
              onReply={next => requireAuth() && setReplyTo(next)}
              onVote={(type, commentId) => requireAuth() && dispatch(normalActions.add_to_comupds(type, userid, commentId))}
              onDelete={handleDeleteComment}
              canDelete={item => String(item.userid) === String(userid)}
              isLast={index === rootComments.length - 1}
            />
          ))}
        </div>
        <CommentComposer
          className="watchCommentComposer"
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          userPhoto={userPhoto}
          userEmail={userEmail}
          onFocusAuth={() => requireAuth()}
          onSubmit={handleAddComment}
        />
      </div>
    </div>,
    document.body
  )
}

export default WatchComments
